import { Box, keyframes } from "@mui/material";
import { LEGACY_GREEN } from "../constants/brandColors";
import { getMm } from "../theme/modernMaterialEnterprise";

/** Ambient motion tints — page color must match authPageOuterSx / CssBaseline background. */
function authAnimationColors(isDarkMode) {
  const mm = getMm(isDarkMode);
  return {
    page: mm.background,
    primary: LEGACY_GREEN.main,
    primarySoft: isDarkMode ? "rgba(76, 175, 80, 0.32)" : "rgba(76, 175, 80, 0.22)",
    primaryFaint: isDarkMode ? "rgba(76, 175, 80, 0.14)" : "rgba(76, 175, 80, 0.1)",
    wavePrimary: isDarkMode ? "rgba(76, 175, 80, 0.38)" : "rgba(76, 175, 80, 0.28)",
    waveSecondary: isDarkMode ? "rgba(76, 175, 80, 0.2)" : "rgba(76, 175, 80, 0.16)",
    waveGray: isDarkMode ? "rgba(73, 69, 79, 0.55)" : "rgba(203, 196, 210, 0.45)",
    grayGlow: isDarkMode ? "rgba(58, 56, 62, 0.75)" : "rgba(232, 232, 232, 0.85)",
    graySoft: isDarkMode ? "rgba(78, 76, 82, 0.45)" : "rgba(214, 214, 214, 0.5)",
    waveMid: isDarkMode ? "rgba(73, 69, 79, 0.45)" : "rgba(203, 196, 210, 0.55)",
  };
}

const WAVE_PERIOD = 1440;
const WAVE_HEIGHT = 160;

/**
 * Two identical periods in one path so translateX(-50%) on a 200%-wide SVG loops seamlessly.
 */
function tiledWavePath(baseY, amplitude) {
  const p = WAVE_PERIOD;
  const h = WAVE_HEIGHT;
  const y0 = baseY;
  const y1 = baseY - amplitude;
  const y2 = baseY + amplitude;
  const one = `M0,${y0} C${p * 0.25},${y1} ${p * 0.75},${y2} ${p},${y0} L${p},${h} L0,${h} Z`;
  const two = `M${p},${y0} C${p * 1.25},${y1} ${p * 1.75},${y2} ${p * 2},${y0} L${p * 2},${h} L${p},${h} Z`;
  return `${one} ${two}`;
}

/** Horizontal scroll only — 0% and 100% show the same tile boundary (no blink). */
const waveScrollForward = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-50%, 0, 0); }
`;

const waveScrollReverse = keyframes`
  from { transform: translate3d(-50%, 0, 0); }
  to { transform: translate3d(0, 0, 0); }
`;

const blobDriftA = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  33% { transform: translate3d(-12%, 6%, 0) scale(1.12); }
  66% { transform: translate3d(8%, -4%, 0) scale(0.92); }
`;

const blobDriftB = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  40% { transform: translate3d(14%, -8%, 0) scale(1.14); }
  70% { transform: translate3d(-10%, 5%, 0) scale(0.9); }
`;

const blobDriftC = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(-8%, -10%, 0) scale(1.1); }
`;

const waveSvgBaseSx = {
  position: "absolute",
  left: 0,
  bottom: 0,
  width: "200%",
  maxWidth: "none",
  height: "100%",
  display: "block",
  willChange: "transform",
  backfaceVisibility: "hidden",
  transform: "translate3d(0, 0, 0)",
};

function WaveLayer({ paths, animation, duration, height = "100%", opacity = 1 }) {
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${WAVE_PERIOD * 2} ${WAVE_HEIGHT}`}
      preserveAspectRatio="none"
      sx={{
        ...waveSvgBaseSx,
        height,
        opacity,
        animation: `${animation} ${duration}s linear infinite`,
      }}
    >
      {paths.map(({ d, fill }, i) => (
        <path key={i} d={d} fill={fill} />
      ))}
    </Box>
  );
}

/**
 * Infinite ambient motion along the bottom of auth pages.
 * Colors follow the neutral gray + green primary design system.
 */
export default function AuthPageBottomAnimation({ isDarkMode }) {
  const c = authAnimationColors(isDarkMode);
  const fadeMask = "linear-gradient(to top, black 0%, black 35%, transparent 100%)";

  const blobBase = {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(40px)",
    willChange: "transform",
  };

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
        "@media (prefers-reduced-motion: reduce)": {
          "& *": {
            animation: "none !important",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: { xs: "55%", sm: "62%" },
          WebkitMaskImage: fadeMask,
          maskImage: fadeMask,
        }}
      >
        {/* Primary green glow */}
        <Box
          sx={{
            ...blobBase,
            width: { xs: "85vw", sm: "58vw" },
            height: { xs: "45vw", sm: "32vw" },
            left: { xs: "-15%", sm: "0%" },
            bottom: { xs: "0%", sm: "4%" },
            background: `radial-gradient(circle, ${c.primarySoft} 0%, transparent 68%)`,
            animation: `${blobDriftA} 16s ease-in-out infinite`,
          }}
        />
        {/* Neutral surface / border tone */}
        <Box
          sx={{
            ...blobBase,
            width: { xs: "75vw", sm: "50vw" },
            height: { xs: "40vw", sm: "28vw" },
            right: { xs: "-12%", sm: "4%" },
            bottom: { xs: "2%", sm: "8%" },
            background: `radial-gradient(circle, ${c.grayGlow} 0%, transparent 70%)`,
            animation: `${blobDriftB} 20s ease-in-out infinite`,
          }}
        />
        {/* Secondary green + gray blend */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: { xs: "0%", sm: "2%" },
            width: { xs: "65vw", sm: "42vw" },
            height: { xs: "35vw", sm: "22vw" },
            transform: "translateX(-50%)",
          }}
        >
          <Box
            sx={{
              ...blobBase,
              inset: 0,
              width: "100%",
              height: "100%",
              background: isDarkMode
                ? `radial-gradient(circle, ${c.primaryFaint} 0%, ${c.graySoft} 45%, transparent 72%)`
                : `radial-gradient(circle, ${c.primaryFaint} 0%, ${c.graySoft} 50%, transparent 72%)`,
              animation: `${blobDriftC} 24s ease-in-out infinite`,
            }}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -1,
            height: { xs: 156, sm: 196 },
            overflow: "hidden",
            transform: "translateZ(0)",
          }}
        >
          <WaveLayer
            duration={28}
            animation={waveScrollForward}
            paths={[
              { d: tiledWavePath(88, 36), fill: c.wavePrimary },
              { d: tiledWavePath(108, 28), fill: c.waveGray },
            ]}
          />
          <WaveLayer
            duration={36}
            animation={waveScrollReverse}
            height="78%"
            paths={[{ d: tiledWavePath(96, 32), fill: c.waveSecondary }]}
          />
          <WaveLayer
            duration={44}
            animation={waveScrollForward}
            height="55%"
            opacity={isDarkMode ? 0.9 : 0.95}
            paths={[{ d: tiledWavePath(112, 24), fill: c.waveMid }]}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: { xs: "38%", sm: "42%" },
          height: "24%",
          background: `linear-gradient(to top, transparent, ${c.page})`,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}
